import { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, TrendingUp, Users, DollarSign } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PartnerApplicationForm from "@/components/vendor/PartnerApplicationForm";
import { useAuth } from "@/contexts/AuthContext";
import { requireUser } from "@/lib/requireUser";

const Sell = () => {
  const [applicationModalOpen, setApplicationModalOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleApplyClick = () => {
    requireUser({
      user,
      navigate,
      currentPath: '/sell',
      action: () => setApplicationModalOpen(true),
      preferSignup: true, // Partners should sign up, not sign in
    });
  };
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-[hsl(201,73%,43%)]/10 to-[hsl(201,73%,43%)]/5">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Partner with Circle Network
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Reach 1,000,000+ real estate agents actively seeking the tools and services you offer
            </p>
            <Button size="lg" className="font-semibold text-lg px-8" onClick={handleApplyClick}>
              Apply to become a partner
            </Button>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Why partner with us?</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-6">
                <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3">Targeted agent audience</h3>
                <p className="text-muted-foreground">
                  Connect with real estate professionals actively searching for CRMs, coaching, marketing services, and business tools
                </p>
              </Card>

              <Card className="p-6">
                <div className="rounded-full bg-accent/10 w-12 h-12 flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-3">Predictable growth</h3>
                <p className="text-muted-foreground">
                  Reach agents at the moment they're comparing options and ready to invest in their business
                </p>
              </Card>

              <Card className="p-6">
                <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mb-4">
                  <DollarSign className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3">Simple partnership</h3>
                <p className="text-muted-foreground">
                  Transparent revenue share model with no listing fees or upfront costs to join
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">How it works</h2>
            
            <div className="max-w-3xl mx-auto space-y-8">
              <div className="flex gap-4">
                <div className="rounded-full bg-primary text-primary-foreground w-8 h-8 flex items-center justify-center font-bold shrink-0">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Apply to partner</h3>
                  <p className="text-muted-foreground">
                    Submit your application and tell us about your service, tool, or coaching program. We'll review and get back to you within 5 business days.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="rounded-full bg-primary text-primary-foreground w-8 h-8 flex items-center justify-center font-bold shrink-0">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Create your listing</h3>
                  <p className="text-muted-foreground">
                    Work with our team to create a compelling listing that showcases how you help agents grow their business.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="rounded-full bg-primary text-primary-foreground w-8 h-8 flex items-center justify-center font-bold shrink-0">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Launch and grow</h3>
                  <p className="text-muted-foreground">
                    Your deal goes live to our audience. Track performance, manage customers, and watch your revenue grow.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Industry Shift Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-6">The way agents buy is changing</h2>
            <p className="text-center text-muted-foreground max-w-3xl mx-auto mb-12 text-lg">
              In real time.
            </p>
            
            <div className="max-w-4xl mx-auto">
              <Card className="p-8 bg-gradient-to-br from-[hsl(201,73%,43%)]/10 to-[hsl(201,73%,43%)]/5 border-[hsl(201,73%,43%)]/20">
                <div className="space-y-6 text-center">
                  <p className="text-xl leading-relaxed">
                    We have finally given agents their power back to choose.
                  </p>
                  <p className="text-xl leading-relaxed">
                    And when they choose <span className="font-bold text-primary">you</span>, your close ratio goes way up—therefore, your cost of acquisition goes way down.
                  </p>
                  <div className="pt-4 border-t border-border">
                    <p className="text-lg font-semibold text-foreground">
                      We are redefining how you find your clients.
                    </p>
                    <p className="text-primary font-bold text-xl mt-2">
                      Join us today.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-foreground text-background">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to reach more agents?</h2>
            <p className="text-xl mb-8 text-background/80">
              Join 50+ real estate service providers already partnering with Circle Network
            </p>
            <Button size="lg" className="font-semibold text-lg px-8" onClick={handleApplyClick}>
              Apply now
            </Button>
          </div>
        </section>
      </main>

      <Dialog open={applicationModalOpen} onOpenChange={setApplicationModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <PartnerApplicationForm onSuccess={() => setApplicationModalOpen(false)} />
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default Sell;
